import React, { useState, useEffect } from 'react';
import {
  Pane,
  Heading,
  Button,
  toaster,
  majorScale,
  TextInput,
  Table,
  Text,
  IconButton,
  TrashIcon,
  PlusIcon,
  EditIcon,
  Dialog,
} from 'evergreen-ui';

function LocationForm({ onLocationAdded }) {
  const [formData, setFormData] = useState({
    countyName: '',
    pickupPoints: [''],
    deliveryFee: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    if (name === 'pickupPoint') {
      const updatedPickupPoints = [...formData.pickupPoints];
      updatedPickupPoints[index] = value;
      setFormData((prev) => ({ ...prev, pickupPoints: updatedPickupPoints }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddPickupPoint = () => {
    setFormData((prev) => ({
      ...prev,
      pickupPoints: [...prev.pickupPoints, ''],
    }));
  };

  const handleRemovePickupPoint = (index) => {
    if (formData.pickupPoints.length > 1) {
      const updatedPickupPoints = formData.pickupPoints.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, pickupPoints: updatedPickupPoints }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const validPickupPoints = formData.pickupPoints.filter((point) => point.trim() !== '');
      if (validPickupPoints.length === 0) {
        throw new Error('At least one valid pickup point is required');
      }

      const promises = validPickupPoints.map((pickupPoint) =>
        fetch('http://localhost:5000/locations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            countyName: formData.countyName,
            pickupPoint: pickupPoint,
            deliveryFee: parseFloat(formData.deliveryFee),
          }),
        }).then(async (response) => {
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to add location');
          }
          return response;
        })
      );

      await Promise.all(promises);

      toaster.success('Locations added successfully!');
      setFormData({ countyName: '', pickupPoints: [''], deliveryFee: '' });
      onLocationAdded();
    } catch (error) {
      toaster.danger('Error adding location: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Pane
      background="white"
      padding={majorScale(3)}
      borderRadius={8}
      elevation={1}
      marginBottom={majorScale(3)}
    >
      <form onSubmit={handleSubmit}>
        <Pane marginBottom={majorScale(2)}>
          <Text display="block" marginBottom={majorScale(1)}>
            County Name <Text color="red">*</Text>
          </Text>
          <TextInput
            name="countyName"
            value={formData.countyName}
            onChange={handleChange}
            placeholder="e.g., Nairobi"
            width="100%"
            required
          />
        </Pane>
        <Pane marginBottom={majorScale(2)}>
          <Text display="block" marginBottom={majorScale(1)}>
            Pickup Points <Text color="red">*</Text>
          </Text>
          {formData.pickupPoints.map((pickupPoint, index) => (
            <Pane key={index} display="flex" alignItems="center" marginBottom={majorScale(1)}>
              <TextInput
                name="pickupPoint"
                value={pickupPoint}
                onChange={(e) => handleChange(e, index)}
                placeholder={`e.g., Pickup Point ${index + 1}`}
                width="100%"
                required
              />
              {formData.pickupPoints.length > 1 && (
                <IconButton
                  icon={TrashIcon}
                  intent="danger"
                  marginLeft={majorScale(1)}
                  onClick={() => handleRemovePickupPoint(index)}
                  title="Remove Pickup Point"
                />
              )}
            </Pane>
          ))}
          <Button
            iconBefore={PlusIcon}
            onClick={handleAddPickupPoint}
            marginTop={majorScale(1)}
          >
            Add Another Pickup Point
          </Button>
        </Pane>
        <Pane marginBottom={majorScale(2)}>
          <Text display="block" marginBottom={majorScale(1)}>
            Delivery Fee (USD) <Text color="red">*</Text>
          </Text>
          <TextInput
            type="number"
            name="deliveryFee"
            value={formData.deliveryFee}
            onChange={handleChange}
            placeholder="e.g., 200.50"
            step="0.01"
            min="0"
            width="100%"
            required
          />
        </Pane>
        <Pane marginTop={majorScale(2)}>
          <Button
            appearance="primary"
            type="submit"
            isLoading={isLoading}
            disabled={
              !formData.countyName ||
              formData.pickupPoints.every((point) => !point.trim()) ||
              !formData.deliveryFee
            }
          >
            Add Location
          </Button>
        </Pane>
      </form>
    </Pane>
  );
}

function LocationList({ onRefresh }) {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    countyName: '',
    pickupPoint: '',
    deliveryFee: '',
  });

  const fetchLocations = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('http://localhost:5000/locations');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch locations');
      }
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      toaster.danger('Error fetching locations: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (location) => {
    setEditFormData({
      countyName: location.countyName,
      pickupPoint: location.pickupPoint,
      deliveryFee: location.deliveryFee.toString(),
    });
    setEditingLocation(location);
  };

  const handleUpdate = async () => {
    if (!editFormData.countyName || !editFormData.pickupPoint || !editFormData.deliveryFee) {
      toaster.danger('All fields are required');
      return;
    }

    try {
      const response = await fetch(`http://localhost:5000/locations/${editingLocation.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          countyName: editFormData.countyName,
          pickupPoint: editFormData.pickupPoint,
          deliveryFee: parseFloat(editFormData.deliveryFee),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update location');
      }

      toaster.success('Location updated successfully!');
      setEditingLocation(null);
      fetchLocations();
      onRefresh();
    } catch (error) {
      toaster.danger('Error updating location: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        const response = await fetch(`http://localhost:5000/locations/${id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete location');
        }

        toaster.success('Location deleted successfully!');
        fetchLocations();
        onRefresh();
      } catch (error) {
        toaster.danger('Error deleting location: ' + error.message);
      }
    }
  };

  // Group locations by countyName
  const groupedLocations = locations.reduce((acc, location) => {
    if (!acc[location.countyName]) {
      acc[location.countyName] = [];
    }
    acc[location.countyName].push(location);
    return acc;
  }, {});

  useEffect(() => {
    fetchLocations();
  }, []);

  return (
    <Pane background="white" padding={majorScale(3)} borderRadius={8} elevation={1}>
      <Heading size={700} marginBottom={majorScale(2)}>
        Pickup Locations
      </Heading>
      {isLoading ? (
        <Text>Loading...</Text>
      ) : Object.keys(groupedLocations).length === 0 ? (
        <Text>No locations found.</Text>
      ) : (
        Object.entries(groupedLocations).map(([county, countyLocations]) => (
          <Pane key={county} marginBottom={majorScale(3)}>
            <Heading size={600} marginBottom={majorScale(1)}>
              {county}
            </Heading>
            <Table>
              <Table.Head>
                <Table.TextHeaderCell>Pickup Point</Table.TextHeaderCell>
                <Table.TextHeaderCell>Delivery Fee (USD)</Table.TextHeaderCell>
                <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
              </Table.Head>
              <Table.Body>
                {countyLocations.map((location) => (
                  <Table.Row key={location.id}>
                    <Table.TextCell>{location.pickupPoint}</Table.TextCell>
                    <Table.TextCell>{location.deliveryFee.toFixed(2)}</Table.TextCell>
                    <Table.Cell>
                      <IconButton
                        icon={EditIcon}
                        marginRight={majorScale(1)}
                        onClick={() => handleEdit(location)}
                        title="Edit Location"
                      />
                      <IconButton
                        icon={TrashIcon}
                        intent="danger"
                        onClick={() => handleDelete(location.id)}
                        title="Delete Location"
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Pane>
        ))
      )}

      <Dialog
        isShown={!!editingLocation}
        title="Edit Location"
        onCloseComplete={() => setEditingLocation(null)}
        confirmLabel="Update"
        onConfirm={handleUpdate}
      >
        <Pane>
          <Pane marginBottom={majorScale(2)}>
            <Text display="block" marginBottom={majorScale(1)}>
              County Name <Text color="red">*</Text>
            </Text>
            <TextInput
              name="countyName"
              value={editFormData.countyName}
              onChange={(e) => setEditFormData({ ...editFormData, countyName: e.target.value })}
              placeholder="e.g., Nairobi"
              width="100%"
              required
            />
          </Pane>
          <Pane marginBottom={majorScale(2)}>
            <Text display="block" marginBottom={majorScale(1)}>
              Pickup Point <Text color="red">*</Text>
            </Text>
            <TextInput
              name="pickupPoint"
              value={editFormData.pickupPoint}
              onChange={(e) => setEditFormData({ ...editFormData, pickupPoint: e.target.value })}
              placeholder="e.g., Pickup Point"
              width="100%"
              required
            />
          </Pane>
          <Pane marginBottom={majorScale(2)}>
            <Text display="block" marginBottom={majorScale(1)}>
              Delivery Fee (USD) <Text color="red">*</Text>
            </Text>
            <TextInput
              type="number"
              name="deliveryFee"
              value={editFormData.deliveryFee}
              onChange={(e) => setEditFormData({ ...editFormData, deliveryFee: e.target.value })}
              placeholder="e.g., 200.50"
              step="0.01"
              min="0"
              width="100%"
              required
            />
          </Pane>
        </Pane>
      </Dialog>
    </Pane>
  );
}

function AdminLocation() {
  const [refreshKey, setRefreshKey] = useState(0);

  const handleLocationAdded = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <Pane
      display="flex"
      flexDirection="column"
      alignItems="center"
      minHeight="100vh"
      background="tint2"
      padding={majorScale(3)}
    >
      <Heading size={900} marginBottom={majorScale(3)}>
        Location Admin Dashboard
      </Heading>
      <Pane width="100%" maxWidth={800}>
        <LocationForm onLocationAdded={handleLocationAdded} />
        <LocationList key={refreshKey} onRefresh={handleRefresh} />
      </Pane>
    </Pane>
  );
}

export default AdminLocation;